import React, { useCallback, useEffect, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { graphql } from 'react-relay';

import Avatar from '@atlaskit/avatar';

import { useContentRefetchDispatch } from '@confluence/content-body/entry-points/ContentRefetchContext.ts';
import { queryInterop } from '@confluence/apollo-relay-interop/entry-points/query.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import type { WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';
import { useSubscriptionsDispatchContext } from '@confluence/graphql/entry-points/SubscriptionsContextProvider.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useDocumentUpdateStatus } from '@confluence/annotation-provider-store/entry-points/DocumentUpdateStatusContext.ts';
import {
	useAllCommentsData,
	getCommentsDataFromCache,
} from '@confluence/comments-data-manager/entry-points/useAllCommentsData.ts';
import { fetchCommentById } from '@confluence/comments-data-manager/entry-points/useCommentById.ts';
import { getProcessedCommentsDataActions } from '@confluence/comments-data-manager/entry-points/useProcessedCommentsData.ts';
import { useCommentService } from '@confluence/content-types-bridge-comments';
import { useUnreadCommentsActions } from '@confluence/unread-comments/entry-points/useUnreadComments.ts';
import { PageMode } from '@confluence/page-utils/entry-points/enums.ts';
import { useInlineCommentsDispatchContext } from '@confluence/comment-context/entry-points/InlineCommentsContext.ts';

import { useInlineReactionActions } from '../inlineReactionStore.ts';
import { extractNumericCommentId } from '../utils/extractNumericCommentId.ts';
import { useSyncLegacyInlineReactionCommentsData } from '../hooks/useSyncLegacyInlineReactionCommentsData.ts';

import type {
	InlineReactionsPubSubHandlerActiveDataRefetchQuery$data,
	InlineReactionsPubSubHandlerActiveDataRefetchQuery$variables,
} from './__generated__/InlineReactionsPubSubHandlerActiveDataRefetchQuery.graphql.ts';
import type {
	InlineReactionsPubSubHandlerActorQuery$data,
	InlineReactionsPubSubHandlerActorQuery$variables,
} from './__generated__/InlineReactionsPubSubHandlerActorQuery.graphql.ts';
import type {
	InlineReactionsPubSubHandlerSummaryRefetchQuery$data,
	InlineReactionsPubSubHandlerSummaryRefetchQuery$variables,
} from './__generated__/InlineReactionsPubSubHandlerSummaryRefetchQuery.graphql.ts';
import {
	removeInlineReactionsPubSub,
	setUpInlineReactionsPubSub,
	type InlineReactionsContentType,
	type InlineReactionsPubSubParams,
} from './inlineReactionsPubSub.ts';

// Three dedicated queries for the pubsub handler. They mirror the queries
// owned by `useReactionsSummary`, `useActiveInlineData`, and `useReactedUsers`
// but are declared here so the handler does not depend on those hooks'
// codegen artefacts. CI's Relay codegen will produce the matching
// `__generated__/...graphql.ts` files on first build.

const inlineReactionsPubSubHandlerSummaryRefetchQuery = graphql`
	query InlineReactionsPubSubHandlerSummaryRefetchQuery(
		$cloudId: ID!
		$contentId: ID!
		$contentType: GraphQLReactionContentType!
	) {
		confluence_contentReactionsSummary(
			cloudId: $cloudId
			contentId: $contentId
			contentType: $contentType
		) {
			reactionsSummary {
				ari
				containerAri
				reactionsCount
				reactionsSummaryForEmoji {
					emojiId
					count
					reacted
				}
			}
		}
	}
`;

const inlineReactionsPubSubHandlerActiveDataRefetchQuery = graphql`
	query InlineReactionsPubSubHandlerActiveDataRefetchQuery($cloudId: ID!, $contentId: ID!) {
		confluence_activeInlineData(cloudId: $cloudId, contentId: $contentId) {
			reactions {
				commentId
				markerRef
				type
			}
		}
	}
`;

const inlineReactionsPubSubHandlerActorQuery = graphql`
	query InlineReactionsPubSubHandlerActorQuery(
		$cloudId: ID!
		$input: ConfluenceReactedUsersInput!
	) {
		confluence_reactedUsers(cloudId: $cloudId, input: $input) {
			confluencePerson {
				accountId
				displayName
				profilePicture {
					path
				}
			}
		}
	}
`;

const i18n = defineMessages({
	newReactionFlagTitle: {
		id: 'experiment-inline-reactions.pubsub.new-reaction-flag-title',
		defaultMessage: 'New reaction',
		description:
			'Title of the notification flag shown when another user adds an inline reaction to a page or blog post the current user is viewing.',
	},
	somebodyReactedToPage: {
		id: 'experiment-inline-reactions.pubsub.somebody-reacted-to-page',
		defaultMessage: '{somebody} reacted to this page',
		description:
			'Description text in the notification flag shown when another user adds an inline reaction to a page the current user is viewing. {somebody} is the actor display name.',
	},
	somebodyReactedToBlog: {
		id: 'experiment-inline-reactions.pubsub.somebody-reacted-to-blog',
		defaultMessage: '{somebody} reacted to this blog post',
		description:
			'Description text in the notification flag shown when another user adds an inline reaction to a blog post the current user is viewing. {somebody} is the actor display name.',
	},
	someoneFallback: {
		id: 'experiment-inline-reactions.pubsub.someone-fallback',
		defaultMessage: 'Someone',
		description:
			'Fallback text used in place of an actor display name when the user that produced the inline reaction event is unknown.',
	},
});

export type InlineReactionsPubSubHandlerProps = {
	contentId: string;
	contentType?: InlineReactionsContentType;
	rendererActions?: InlineReactionsPubSubParams['rendererActions'];
	updateDocument?: InlineReactionsPubSubParams['updateDocument'];
	allowDocumentMutation?: boolean;
	pageMode?: PageMode;
	syncEditorInlineReactionAnnotation?: InlineReactionsPubSubParams['syncEditorInlineReactionAnnotation'];
};

type Props = InlineReactionsPubSubHandlerProps & WithFlagsProps;

const InlineReactionsPubSubHandlerComponent: React.FC<Props> = ({
	contentId,
	contentType = 'page',
	rendererActions,
	updateDocument,
	allowDocumentMutation = true,
	pageMode = PageMode.VIEW,
	syncEditorInlineReactionAnnotation,
	flags,
}) => {
	const intl = useIntl();
	const { cloudId, userId } = useSessionData();
	const {
		addOptimisticReaction,
		pruneOptimisticReactions,
		applyRemoteDelta,
		markReactedUsersChanged,
	} = useInlineReactionActions();
	const { addCommentInCache, deleteCommentInCache } = useAllCommentsData({
		contentId,
		options: { skip: false },
	});
	const { updateUnreadCommentIDSet } = useUnreadCommentsActions();
	const { commentService } = useCommentService();
	const { setActiveHighlight } = useInlineCommentsDispatchContext() || {};
	const dispatchContext = useSubscriptionsDispatchContext();
	const { setRefetchContent } = useContentRefetchDispatch();
	const { publishedDocumentVersion } = useDocumentUpdateStatus();
	// Track the latest doc version in a ref so the handler closure always
	// reads the current value without causing the subscription to be torn
	// down and re-established on every version bump.
	const publishedDocumentVersionRef = useRef(publishedDocumentVersion);
	publishedDocumentVersionRef.current = publishedDocumentVersion;
	const subscriptionsRef = useRef<Array<{ subscriberId: string; delta: string }>>([]);
	const showCustomFlagRef = useRef(flags.showCustomFlag);
	showCustomFlagRef.current = flags.showCustomFlag;
	const addOptimisticReactionRef = useRef(addOptimisticReaction);
	addOptimisticReactionRef.current = addOptimisticReaction;
	const pruneOptimisticReactionsRef = useRef(pruneOptimisticReactions);
	pruneOptimisticReactionsRef.current = pruneOptimisticReactions;
	const applyRemoteDeltaRef = useRef(applyRemoteDelta);
	applyRemoteDeltaRef.current = applyRemoteDelta;
	const markReactedUsersChangedRef = useRef(markReactedUsersChanged);
	markReactedUsersChangedRef.current = markReactedUsersChanged;
	const syncLegacyCommentsDataReactionsSummaryRef =
		useRef<InlineReactionsPubSubParams['syncLegacyCommentsDataReactionsSummary']>();
	const syncLegacyCommentsDataReactionThreadRef =
		useRef<InlineReactionsPubSubParams['syncLegacyCommentsDataReactionThread']>();
	const addCommentInCacheRef = useRef(addCommentInCache);
	addCommentInCacheRef.current = addCommentInCache;
	const deleteCommentInCacheRef = useRef(deleteCommentInCache);
	deleteCommentInCacheRef.current = deleteCommentInCache;
	const updateUnreadCommentIDSetRef = useRef(updateUnreadCommentIDSet);
	updateUnreadCommentIDSetRef.current = updateUnreadCommentIDSet;
	const setActiveHighlightRef = useRef(setActiveHighlight);
	setActiveHighlightRef.current = setActiveHighlight;
	const syncEditorInlineReactionAnnotationRef = useRef(syncEditorInlineReactionAnnotation);
	syncEditorInlineReactionAnnotationRef.current = syncEditorInlineReactionAnnotation;
	const commentServiceRef = useRef(commentService);
	commentServiceRef.current = commentService;
	const addSubscriberRef = useRef(dispatchContext?.addSubscriber);
	addSubscriberRef.current = dispatchContext?.addSubscriber;
	const removeSubscriberRef = useRef(dispatchContext?.removeSubscriber);
	removeSubscriberRef.current = dispatchContext?.removeSubscriber;
	const setContentIdRef = useRef(dispatchContext?.setContentId);
	setContentIdRef.current = dispatchContext?.setContentId;
	const setContentTypeRef = useRef(dispatchContext?.setContentType);
	setContentTypeRef.current = dispatchContext?.setContentType;

	const markDocumentNeedsUpdate = useCallback(() => {
		if (!allowDocumentMutation) {
			return;
		}
		// Triggers a full page-content refetch to deliver a new annotation
		// mark when it cannot be applied directly from the pubsub event
		// (e.g. position data is absent, or the receiver's doc version
		// doesn't match — see `createInlineReactionAddedHandler`).
		setRefetchContent(true);
	}, [allowDocumentMutation, setRefetchContent]);

	// Re-issue the same query nodes that `useReactionsSummary` and
	// `useActiveInlineData` use. `queryInterop` writes the response into the
	// shared cache against the same key those hooks watch, so any active
	// subscriber instance re-renders with the fresh data automatically.

	const refetchReactionsSummary = useCallback(
		(commentId: string) => {
			const variables: InlineReactionsPubSubHandlerSummaryRefetchQuery$variables = {
				cloudId,
				contentId: extractNumericCommentId(commentId),
				contentType: 'COMMENT',
			};
			void queryInterop<
				InlineReactionsPubSubHandlerSummaryRefetchQuery$data,
				InlineReactionsPubSubHandlerSummaryRefetchQuery$variables
			>({
				query: inlineReactionsPubSubHandlerSummaryRefetchQuery,
				variables,
				fetchPolicy: 'network-only',
			}).catch(() => {
				// Network failure — leave the cached entry as-is. A
				// subsequent user-initiated refetch will recover.
			});
		},
		[cloudId],
	);

	const refetchActiveInlineData = useCallback(() => {
		// `confluence_activeInlineData` expects a content (page/blogpost) ARI,
		// not the comment ARI. Build it from the resource type the handler
		// was mounted with.
		const resource = contentType === 'blogpost' ? 'blogpost' : 'page';
		const variables: InlineReactionsPubSubHandlerActiveDataRefetchQuery$variables = {
			cloudId,
			contentId: `ari:cloud:confluence:${cloudId}:${resource}/${contentId}`,
		};
		void queryInterop<
			InlineReactionsPubSubHandlerActiveDataRefetchQuery$data,
			InlineReactionsPubSubHandlerActiveDataRefetchQuery$variables
		>({
			query: inlineReactionsPubSubHandlerActiveDataRefetchQuery,
			variables,
			fetchPolicy: 'network-only',
		}).catch(() => {
			// Network failure — leave the cached entry as-is.
		});
	}, [cloudId, contentId, contentType]);

	const { syncLegacyCommentsDataReactionsSummary, syncLegacyCommentsDataReactionThread } =
		useSyncLegacyInlineReactionCommentsData({
			contentId,
			currentUserId: userId,
		});
	syncLegacyCommentsDataReactionsSummaryRef.current = syncLegacyCommentsDataReactionsSummary;
	syncLegacyCommentsDataReactionThreadRef.current = syncLegacyCommentsDataReactionThread;

	const showReactionFlag = useCallback(
		({
			commentId,
			emojiId,
			actorAccountId,
			contentType: flagContentType,
		}: {
			commentId: string;
			markerRef: string;
			emojiId?: string;
			contentType: InlineReactionsContentType;
			actorAccountId?: string;
		}) => {
			const buildDescription = (somebody: string) =>
				intl.formatMessage(
					flagContentType === 'blogpost' ? i18n.somebodyReactedToBlog : i18n.somebodyReactedToPage,
					{ somebody },
				);

			const fallbackName = intl.formatMessage(i18n.someoneFallback);
			const flagId = `inline-reactions-pubsub-${commentId}`;

			const presentFlag = (somebody: string, avatarSrc?: string) => {
				void showCustomFlagRef.current({
					id: flagId,
					title: intl.formatMessage(i18n.newReactionFlagTitle),
					description: buildDescription(somebody),
					isAutoDismiss: true,
					...(avatarSrc
						? { customIcon: <Avatar src={avatarSrc} name={somebody} size="small" /> }
						: {}),
				});
			};

			// If we don't have enough info to resolve the actor name, just show
			// the fallback flag immediately — there's nothing to wait on.
			if (!emojiId || !actorAccountId) {
				presentFlag(fallbackName);
				return;
			}

			const containerType = contentType === 'blogpost' ? 'BLOGPOST' : 'PAGE';
			const variables: InlineReactionsPubSubHandlerActorQuery$variables = {
				cloudId,
				input: {
					contentId: extractNumericCommentId(commentId),
					contentType: 'COMMENT',
					containerId: contentId,
					containerType,
					emojiId,
				},
			};

			void queryInterop<
				InlineReactionsPubSubHandlerActorQuery$data,
				InlineReactionsPubSubHandlerActorQuery$variables
			>({
				query: inlineReactionsPubSubHandlerActorQuery,
				variables,
			})
				.then(({ data }) => {
					const persons = data?.confluence_reactedUsers?.confluencePerson ?? [];
					const actor = persons.find((p): p is NonNullable<typeof p> =>
						Boolean(p?.accountId && p.accountId === actorAccountId),
					);
					const displayName = actor?.displayName?.trim();
					const avatarSrc = actor?.profilePicture?.path ?? undefined;
					presentFlag(displayName || fallbackName, avatarSrc);
				})
				.catch(() => {
					presentFlag(fallbackName);
				});
		},
		[cloudId, contentId, contentType, intl],
	);

	const hasSubscriptionDispatch =
		Boolean(dispatchContext?.addSubscriber) &&
		Boolean(dispatchContext?.removeSubscriber) &&
		Boolean(dispatchContext?.setContentId) &&
		Boolean(dispatchContext?.setContentType);

	useEffect(() => {
		// `confluence_enable_subscriptions-context-provider` is at 100% rollout
		// (cleanup phase), so we don't gate on it here — adding a new use of a
		// stale gate would just have to be cleaned up again. The dispatch
		// helpers below are the practical signal that the central subscription
		// is wired up.
		if (!expValEquals('confluence_inline_reactions', 'isEnabled', true)) {
			return;
		}
		if (!hasSubscriptionDispatch) {
			return;
		}
		if (!contentId) {
			return;
		}

		const addSubscriber = addSubscriberRef.current;
		const removeSubscriber = removeSubscriberRef.current;
		const setContentId = setContentIdRef.current;
		const setContentType = setContentTypeRef.current;

		if (!addSubscriber || !removeSubscriber || !setContentId || !setContentType) {
			return;
		}

		subscriptionsRef.current = setUpInlineReactionsPubSub({
			contentId,
			contentType,
			currentUserId: userId,
			rendererActions,
			updateDocument,
			publishedDocumentVersionRef,
			allowDocumentMutation,
			pageMode,
			addOptimisticReaction: (...args) => addOptimisticReactionRef.current(...args),
			pruneOptimisticReactions: (...args) => pruneOptimisticReactionsRef.current(...args),
			applyRemoteDelta: (...args) => applyRemoteDeltaRef.current(...args),
			markReactedUsersChanged: (...args) => markReactedUsersChangedRef.current(...args),
			setActiveHighlight: (markerRef) => setActiveHighlightRef.current?.(markerRef),
			syncLegacyCommentsDataReactionsSummary: (...args) =>
				syncLegacyCommentsDataReactionsSummaryRef.current?.(...args),
			syncLegacyCommentsDataReactionThread: (...args) =>
				syncLegacyCommentsDataReactionThreadRef.current?.(...args),
			refetchReactionsSummary,
			refetchActiveInlineData,
			markDocumentNeedsUpdate,
			showReactionFlag,
			addCommentInCache: (...args) => addCommentInCacheRef.current?.(...args),
			deleteCommentInCache: (...args) => deleteCommentInCacheRef.current?.(...args),
			fetchCommentById,
			updateUnreadCommentIDSet: (...args) => updateUnreadCommentIDSetRef.current(...args),
			getCommentsDataFromCache,
			getProcessedCommentsDataActions,
			syncEditorInlineReactionAnnotation: (...args) =>
				syncEditorInlineReactionAnnotationRef.current?.(...args),
			triggerCommentPanelRefresh: () => commentServiceRef.current.triggerCommentPanelRefresh(),
			addSubscriber,
			setContentId,
			setContentType,
		});

		return () => {
			removeInlineReactionsPubSub({
				subscriptions: subscriptionsRef.current,
				removeSubscriber,
			});
			subscriptionsRef.current = [];
		};
	}, [
		contentId,
		contentType,
		userId,
		rendererActions,
		updateDocument,
		allowDocumentMutation,
		pageMode,
		hasSubscriptionDispatch,
		refetchReactionsSummary,
		refetchActiveInlineData,
		markDocumentNeedsUpdate,
		showReactionFlag,
	]);

	return null;
};

InlineReactionsPubSubHandlerComponent.displayName = 'InlineReactionsPubSubHandler';

export const InlineReactionsPubSubHandler = withFlags(InlineReactionsPubSubHandlerComponent);
